import { Box, Button } from '@mui/material'
import React from 'react'
import { RuntimeContext } from '../library/NGFieldExtensions';
import { Component, Maybe, ReviewDialog } from '../../resolvers-types';
import NGReviewDialog from '../components/NGReviewDialog/NGReviewDialog';
import NGIcon from '../components/NGIcon/NGIcon';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import { inUserFeed } from '../library/utils';
import { GetFeedback } from '../library/components';
import { isNil } from 'lodash-es';
import { log } from '../library/logger';
import { onFeedbackAction, reviewsActions } from '../components/NGReviewDialog/NGReviewDialogPopups';
import { getState } from '../library/dataService';
import "../components/NGReviewDialog/useReviewDialog.css";
import { executeAction } from '../library/actions';

const tag = "NGReviewDialog";

async function retrieveComponentFeedback(componentId, userId) {
  const feedback = await GetFeedback(componentId, userId);

  if (isNil(feedback)) log.debug(tag, `Reviews for component '${componentId}' not found`);

  return feedback;
}

function getReviewDialogOptions(): Maybe<ReviewDialog> {
  return {
    Visible: true,
    ShowReaction: true,
    ShowTextNotes: true,
    ShowVoiceNotes: true,
    ShowInfo: true,
    ShowComments: false,
    Actions: reviewsActions,
    Bindings: {
      Data: "State.FeedbackData",
    }
  }
}

export default function useReviewDialog(config: Component, context: RuntimeContext, inReviewMode: boolean) {
  const showReviewDialog = useSignal(inUserFeed());
  const { global } = getState(context);
  const user = global?.User.value;
  const ReviewDialogConfig = useSignal<Maybe<ReviewDialog> | undefined>(getReviewDialogOptions());

  const handleToggleReviewDialog = () => {
    showReviewDialog.value = !showReviewDialog.value;
  }

  useSignalEffect(() => {
    if (showReviewDialog.value && inReviewMode) {
      retrieveComponentFeedback(config.ComponentId, user?.Id).then((feedback) => {
        executeAction(onFeedbackAction, null, {
          Item: feedback?.Item || {
            Id: config.ComponentId ?? "",
            Name: config.Name,
            SerialNumber: config.SerialNumber,
            Type: config.Type ?? "",
            __typename: "Component",
          },
          NoteCount: feedback?.ReviewerData?.length || 0,
          Reaction: feedback?.ReviewerData?.[0]?.Reaction,
          Notes: feedback?.ReviewerData?.[0]?.Notes,
          Reviewer: feedback?.ReviewerData?.[0]?.Reviewer || user?.Id,
        }, context)
        log.info(tag, `Feedback retrieved: ${feedback}`);
      })
        .catch((error) => {
          log.error(tag, `Error retrieving feedback: ${error}`);
        });
    }
  });

  const withReviewDialog = (reactComponent?: React.ReactNode) => {
    return (
      <Box sx={{
        border: showReviewDialog.value ? '1px solid var(--md-sys-color-primary)' : 'none',
        borderRadius: '8px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        <Box className="reviewIconContainer" onClick={() => handleToggleReviewDialog()}>
          <Button
            sx={{
              margin: 0,
              padding: 0,
              span: {
                margin: 0,
                padding: 0,
              }
            }}
            startIcon={
              <NGIcon
                config={{
                  Id: "ReviewDialog_Icon",
                  IconName: "ChatOutlined",
                  Style: {
                    color: "var(--md-sys-color-primary)",
                  }
                }}
                context={context}
              />
            }
          />
        </Box>

        {reactComponent}
        {showReviewDialog.value && ReviewDialogConfig.value && (
          <NGReviewDialog config={ReviewDialogConfig.value} context={context} />
        )}
      </Box>
    )
  }

  return withReviewDialog;
}
